import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Icon, Paginator, Text } from '@quintoandar-tokko/jopijs';
import Trans from '@/components/shared/Trans';
import translations from '@/components/Dashboard/Dashboard.trans.json';
import buildQuery from '@/components/Dashboard/utils/buildQuery';
import buildTableQuery from '@/components/Dashboard/utils/buildTableQuery';
import {
  clearPerformanceModalFilters,
  setPerformanceModals,
} from '@/store/slices/components/dashboard/DashboardSlice';
import { getPerformanceAgentsPerformanceTable } from '@/store/slices/components/dashboard/service';
import styles from './agentsPerformance.module.scss';
import modalStyles from '../PerformanceModals/performanceModals.module.scss';
import SearchInput from '../PerformanceModals/SearchInput';
import Checkbox from '@/components/checkbox/Checkbox';
import UseShowGroupByBranch from '@/components/Dashboard/utils/UseShowGroupByBranch';
import TableDropdown from '@/components/Dashboard/DashboardTables/components/TableDropdown';
import { agentsPerformanceStatusOptions } from '../PerformanceModals/variants/utils';
import { AGENT } from '@/components/FilterBar/constants/filtersConstants';
import DownloadButton from '@/components/Dashboard/DashboardTables/components/DownloadButton';
import useAsyncTableExcelDownload from '@/components/Dashboard/customHooks/useAsyncTableExcelDownload';
import { TABLES } from '@/components/Dashboard/constants';
import TableSearchEmptyState from '@/components/Dashboard/DashboardTables/TableSearchEmptyState';
import AgentsPerformanceTable from '@/components/Dashboard/DashboardTables/AgentsPerformanceTable';
import useTrackPerformanceTable from '@/components/Dashboard/customHooks/useTrackDashboard';
import { TRACK_ELEMENT } from '@/components/Dashboard/constants/track';
import useFetchDashboard from '../../customHooks/useFetchDashboard';
import { useDndCardContext } from '../../DndCard/context';

const headerId = 'agents-performance-header';

export default function AgentsPerformance() {
  const dispatch = useDispatch();
  const { selectedIds, selectAgents, selectBranches } = useSelector(
    (state) => state.FilterBarSlice.filters
  );
  const { edition } = useSelector((state) => state.DashboardSlice);
  const { agentsPerformance } = useSelector(
    (state) => state.DashboardSlice.performanceModals
  );
  const { visible: dndVisible } = useDndCardContext();
  const {
    list,
    loading,
    hasFetched,
    sortBy,
    sortDirection,
    searchQuery,
    searchStatus,
    groupByBranch,
    page,
  } = agentsPerformance;

  const { ref, isStale, useFetchOnViewport } = useFetchDashboard();
  const isLoading = loading || isStale;
  const showGroupByBranch = UseShowGroupByBranch();
  const track = useTrackPerformanceTable();
  const { handleDownloadExcel: downloadExcel, isDownloading } =
    useAsyncTableExcelDownload(TABLES.AGENTS_PERFORMANCE);
  const isHeaderReady = Boolean(document.querySelector(`#${headerId}`));

  const uniqueAgent =
    selectedIds.filter((x) => x.filter_type === AGENT).length === 1;

  const selectedStatusOption =
    agentsPerformanceStatusOptions.find((opt) => opt.value === searchStatus) ??
    agentsPerformanceStatusOptions[1];

  useFetchOnViewport(
    (query) => {
      const tableQuery = buildTableQuery(agentsPerformance, {
        page,
      });

      dispatch(getPerformanceAgentsPerformanceTable({ query, tableQuery }));
    },
    [
      selectedIds,
      sortBy,
      sortDirection,
      searchQuery,
      searchStatus,
      groupByBranch,
      page,
    ],
    { hasFetched }
  );

  const handleDownloadExcel = () => {
    const query = buildQuery(selectedIds);
    const tableQuery = buildTableQuery(agentsPerformance);
    downloadExcel(query, tableQuery);
    track.agentsPerformance(TRACK_ELEMENT.DOWNLOAD_BUTTON);
  };

  const handleClearFilters = () => {
    const modal = 'agentsPerformance';
    const filters = ['searchQuery', 'searchStatus', 'groupByBranch'];
    dispatch(clearPerformanceModalFilters({ modal, filters }));
    dispatch(
      setPerformanceModals({
        key: 'agentsPerformance',
        subkey: 'page',
        value: 1,
      })
    );
  };

  const handleBranchGroupCheck = () => {
    dispatch(
      setPerformanceModals({
        key: 'agentsPerformance',
        subkey: 'groupByBranch',
        value: !groupByBranch,
      })
    );
    dispatch(
      setPerformanceModals({
        key: 'agentsPerformance',
        subkey: 'page',
        value: 1,
      })
    );
    if (track.agentsPerformance)
      track.agentsPerformance(TRACK_ELEMENT.BRANCH_GROUP_CHECKBOX);
  };

  const handleAgentBranchChange = (option) => {
    dispatch(
      setPerformanceModals({
        key: 'agentsPerformance',
        subkey: 'searchStatus',
        value: option.value,
      })
    );
    dispatch(
      setPerformanceModals({
        key: 'agentsPerformance',
        subkey: 'page',
        value: 1,
      })
    );
    if (track.agentsPerformance)
      track.agentsPerformance(TRACK_ELEMENT.DROPDOWN_SHOW_ALL);
  };

  const handleChangePage = (newPage) => {
    dispatch(
      setPerformanceModals({
        key: 'agentsPerformance',
        subkey: 'page',
        value: newPage,
      })
    );
  };

  return (
    <Box className={styles.agentsPerformance} id="agents-performance" ref={ref}>
      <Box className={styles.header} id={headerId}>
        <Box className={styles.headerLeft}>
          {edition.active && dndVisible && (
            <Icon
              className={styles.hiddenIcon}
              icon="icon-menu"
              fontSize="12px"
            />
          )}
          <Text className={styles.title}>
            {Trans(translations, 'AgentsPerformance')}
          </Text>
        </Box>
      </Box>

      <Box className={modalStyles.modalControls}>
        {!uniqueAgent && (
          <SearchInput
            modal="agentsPerformance"
            maxWidth={selectBranches.length > 1 ? 520 : '100%'}
          />
        )}

        {showGroupByBranch && (
          <Box
            className={modalStyles.checkboxWrapper}
            onClick={handleBranchGroupCheck}
          >
            <Checkbox checked={groupByBranch} />
            <Box as="span" className={modalStyles.checkboxText}>
              {Trans(translations, 'GroupByBranch')}
            </Box>
          </Box>
        )}

        {(selectAgents.length > 1 || selectBranches.length > 1) &&
          !uniqueAgent && (
            <Box className={modalStyles.dropdownWrapper}>
              <Text variant={modalStyles.dropdownWrapperLabel}>
                {Trans(translations, 'NewContactsTable.Show')}
              </Text>
              <TableDropdown
                selectedOption={selectedStatusOption}
                options={agentsPerformanceStatusOptions}
                onSelect={(option) => handleAgentBranchChange(option)}
              />
            </Box>
          )}

        {!uniqueAgent && (
          <DownloadButton
            onClick={handleDownloadExcel}
            disabled={isDownloading}
          />
        )}
      </Box>

      {searchQuery && list?.length === 0 && !isLoading ? (
        <TableSearchEmptyState onClearFilters={handleClearFilters} />
      ) : (
        <>
          <Box
            className={`${modalStyles.tableContainer} ${modalStyles.tableContainerCard}`}
          >
            <AgentsPerformanceTable />
          </Box>

          {uniqueAgent &&
            isHeaderReady &&
            createPortal(
              <DownloadButton
                onClick={handleDownloadExcel}
                disabled={isDownloading}
              />,
              document.querySelector(`#${headerId}`)
            )}

          {agentsPerformance.num_pages > 1 &&
            !agentsPerformance.groupByBranch && (
              <Box className={styles.footer} data-disabled={isLoading}>
                <Paginator
                  currentPage={agentsPerformance.page}
                  total={agentsPerformance.count}
                  perPage={agentsPerformance.per_page}
                  changePageNumber={handleChangePage}
                />
              </Box>
            )}
        </>
      )}
    </Box>
  );
}
